import _ from 'lodash';
import React, {FC, useState} from 'react';
import styled, {css} from 'styled-components';

import {useDebounce} from '../../../../libs/shared/helpers/src/react/hooks/hookHelpers';
import {HandlerOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {stretch} from '../../../../libs/shared/ui/src/styles/mixins';
import {UserThemeConsumer} from '../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {LocalPreferencesSizes, LocalPreferencesSizesEnum} from '../../../models/api/localPreferencesModel';
import {SizeUpdater} from '../../../store/data/localPreferences/localPreferencesActions';
import {
  maxCellsPanelWidth,
  minCellsPanelWidth,
} from '../../../store/panels/cells/helpers/cellsPanelPathHelpers';
import {InboxesParameters} from '../../../store/panels/inboxes/parameters/inboxesParameters';
import {InboxesPanelId, makeCellsPanelId, makeViewerPanelId} from '../../../store/panels/panelStateBase';
import {GridItem} from '../../common/grid';
import {ResizeSeparator} from '../../common/resizeSeparator';
import {CellsContainer} from '../../inboxes/cells/cellsContainer';
import {VisitorConversationsViewerContainer} from '../viewer/visitorConversationsViewerContainer';

/*
 * Props.
 */

export interface VisitorConversationsProps {
  panelId: InboxesPanelId;
  sizes: LocalPreferencesSizes;
  updateSize: SizeUpdater;
  onParametersChange: HandlerOf<InboxesParameters>;
  hasVisitorExperienceV3: boolean;
  conversations: any;
}

interface StyledCellsShowP2ConversationProps {
  $showP2?: boolean;
}

/*
 * Style.
 */

const StyledGridDiv = styled.div<StyledCellsShowP2ConversationProps>`
  display: grid;
  grid-auto-flow: column;
  grid-template-rows: 100%;
  height: 100%;

  ${(p) =>
    p.$showP2
      ? css`
          grid-template-columns: auto auto 1fr;
          min-height: 250px;
        `
      : css`
          grid-template-columns: auto;
        `}
`;

const StyledStretchGridItem = styled(GridItem)`
  ${stretch()}
`;

/*
 * Component.
 */

export const VisitorConversations: FC<VisitorConversationsProps> = (props) => {
  const {panelId, onParametersChange, hasVisitorExperienceV3, conversations} = props;
  const cellsPanelId = makeCellsPanelId(panelId);
  const [cellsWidth, setCellsWidth] = useState(props.sizes[LocalPreferencesSizesEnum.CELLS]);

  const onCellsWidthChange: HandlerOf<number> = (newCellsWidth) => {
    setCellsWidth(newCellsWidth);
    saveCellsWidthDebounce(newCellsWidth);
  };

  const saveCellsWidthDebounce = useDebounce((newWidth: number) => {
    props.updateSize(LocalPreferencesSizesEnum.CELLS, newWidth);
  }, 1000);

  const conversationsCount = _.size(conversations);

  if (hasVisitorExperienceV3 && conversationsCount === 1) {
    return (
      <StyledGridDiv $showP2={false}>
        <VisitorConversationsViewerContainer
          cellsPanelId={cellsPanelId}
          panelId={makeViewerPanelId(panelId)}
          hasVisitorExperienceV3={hasVisitorExperienceV3}
        />
      </StyledGridDiv>
    );
  }
  return (
    <StyledGridDiv $showP2>
      <StyledStretchGridItem column={1} row={1} style={{width: cellsWidth}}>
        <CellsContainer panelId={cellsPanelId} onParametersChange={onParametersChange} />
      </StyledStretchGridItem>
      <UserThemeConsumer>
        {(theme) => (
          <ResizeSeparator
            column={2}
            row={1}
            size={cellsWidth}
            minSize={minCellsPanelWidth}
            maxSize={maxCellsPanelWidth}
            onSizeChange={onCellsWidthChange}
            color={theme.separator}
          />
        )}
      </UserThemeConsumer>
      <StyledStretchGridItem column={3} row={1}>
        <VisitorConversationsViewerContainer
          cellsPanelId={cellsPanelId}
          panelId={makeViewerPanelId(panelId)}
          hasVisitorExperienceV3={hasVisitorExperienceV3}
        />
      </StyledStretchGridItem>
    </StyledGridDiv>
  );
};
